import useTranslate from "../../../core/languages/hooks/useTranslate";
import React, {useEffect, useRef, useState} from "react";
import ClearDataButton from "./ClearDataButton";
import {BookingFilters} from "../../../booking/hooks/useBookingMeSearchData";

interface Props {
    query: BookingFilters;
    onChange: (updates: Partial<BookingFilters>) => void;
}

const YearFilter: React.FC<Props> = ({query, onChange}) => {
    const t = useTranslate();
    const yearRef = useRef<HTMLDivElement>(null);
    const [showYearPopover, setShowYearPopover] = useState(false);
    const [selectedYear, setSelectedYear] = useState<string | undefined>();

    const currentYear = new Date().getFullYear();
    const years = Array.from({length: 10}, (_, i) => (currentYear - i).toString());
    const handleSelectYear = (value: string) => {
        setSelectedYear(value);
        setShowYearPopover(false);
        const year = Number(value);
        const arrival = new Date(Date.UTC(year, 0, 1, 0, 0, 0));
        const departure = new Date(Date.UTC(year, 11, 31, 23, 59, 59));
        onChange({
            arrivalDate: {after: arrival},
            departureDate: {before: departure}
        });
    };

    const handleClear = () => {
        setSelectedYear(undefined);
        onChange({
            arrivalDate: {after: undefined},
            departureDate: {before: undefined}
        });
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (yearRef.current && !yearRef.current.contains(e.target as Node)) {
            setShowYearPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const renderPopoverOptions = (options: string[]) =>
        options.map(opt => (
            <div
                key={opt}
                className={`flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer ${
                    selectedYear === opt ? "bg-neutral-200 dark:bg-neutral-700" : ""
                }`}
                onClick={() => handleSelectYear(opt)}
            >
                <span className="block font-medium text-neutral-700 dark:text-neutral-200">{opt}</span>
            </div>
        ));

    return (
        <div
            ref={yearRef}
            className="relative flex flex-[1.5] cursor-pointer [ nc-hero-field-padding ] items-center"
        >
            <div className="flex-grow">
                <div
                    className="w-full p-0 xl:text-lg font-semibold truncate"
                    onClick={() => setShowYearPopover(v => !v)}
                >
                    {selectedYear
                        ? <span className="text-neutral-800 dark:text-neutral-100">{selectedYear}</span>
                        :
                        <span className="text-neutral-800 dark:text-neutral-200">{t("bookingMeForm.year")}</span>
                    }
                </div>
                <span
                    className="block mt-0.5 text-sm text-neutral-400 font-light">{t("bookingMeForm.searchByYear")}</span>
            </div>

            {selectedYear && <ClearDataButton onClick={handleClear}/>}

            {showYearPopover && (
                <div
                    className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                    {renderPopoverOptions(years)}
                </div>
            )}
        </div>
    );
};

export default YearFilter;
